import { useState } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ClueLanguageFlagsEditorProps {
  clueId: number;
  language: number;
  onLanguageChange?: (newLanguage: number) => void;
  className?: string;
}

// Bit flags: 1=Swedish, 2=English, 4=German, 8=French, 16=Spanish, 32=Italian
const SWEDISH = 1;
const ENGLISH = 2;
const GERMAN = 4;
const FRENCH = 8;
const SPANISH = 16;
const ITALIAN = 32;

const LANGUAGES = [
  { flag: "🇸🇪", value: SWEDISH, name: "Svenska" },
  { flag: "🇬🇧", value: ENGLISH, name: "Engelska" },
  { flag: "🇩🇪", value: GERMAN, name: "Tyska" },
  { flag: "🇫🇷", value: FRENCH, name: "Franska" },
  { flag: "🇪🇸", value: SPANISH, name: "Spanska" },
  { flag: "🇮🇹", value: ITALIAN, name: "Italienska" },
];

export const ClueLanguageFlagsEditor = ({ 
  clueId, 
  language, 
  onLanguageChange,
  className 
}: ClueLanguageFlagsEditorProps) => {
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [isUpdating, setIsUpdating] = useState(false);

  const toggleLanguage = async (langValue: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isUpdating) return;

    const newLanguage = currentLanguage ^ langValue; // XOR to toggle bit
    
    // Prevent setting language to 0 (no language selected)
    if (newLanguage === 0) {
      toast.error("Minst ett språk måste vara valt");
      return;
    }

    setIsUpdating(true);

    try {
      const { error } = await supabase
        .from('clues')
        .update({ language: newLanguage })
        .eq('id', clueId);

      if (error) throw error;

      setCurrentLanguage(newLanguage);
      onLanguageChange?.(newLanguage);
      toast.success("Språk uppdaterat för ledtråd");
    } catch (error) {
      console.error('Error updating clue language:', error);
      toast.error("Kunde inte uppdatera språk");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <span 
      className={cn(
        "inline-flex gap-1 text-sm",
        isUpdating && "opacity-50",
        className
      )}
    >
      {LANGUAGES.map((lang) => {
        const isActive = (currentLanguage & lang.value) !== 0;
        return (
          <button
            key={lang.value}
            onClick={(e) => toggleLanguage(lang.value, e)}
            disabled={isUpdating}
            className={cn(
              "transition-all cursor-pointer hover:scale-110",
              isActive 
                ? "opacity-100" 
                : "opacity-30 grayscale"
            )}
            title={`${isActive ? "Ta bort" : "Lägg till"} ${lang.name}`}
          >
            {lang.flag}
          </button>
        );
      })}
    </span>
  );
};
